import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  buildDisplayTiers,
  formatTierLabel,
} from "../utils/cancellationPolicyTiers";
import {
  NON_REFUNDABLE_PROCESSING_FEE_NOTICE,
  NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE,
} from "../utils/transactionPolicyText";

/**
 * CancellationPolicyModal
 * Fetches platform cancellation policy and shows it in a modal overlay.
 *
 * ALSO THE RESCHEDULE POLICY, AND THE PLACE THE CUSTOMER ACCEPTS BOTH (2026-11-04).
 *
 * The owner: *"I want them to open the cancellation & reschedule policy and check the box in the modal, so they
 * don't need to check the box on the summary page but the policy modal, and it will still record the
 * acknowledgement as it is."* So the acknowledgement moved IN here. The summary page keeps a strip that says
 * whether it has been accepted and a link back to this modal; the recorded value is unchanged
 * (`policyAcceptedAt` still rides on the booking payload).
 *
 * Props:
 *   open            - boolean, whether the modal is visible
 *   onClose         - callback to close the modal
 *   policy          - optional pre-fetched policy object (avoids extra network call)
 *   acknowledgement - optional { accepted, onAccept } — when given, the modal shows the accept checkbox and
 *                     will not close on "Got it" until it is ticked
 */
function CancellationPolicyModal({ open, onClose, policy: propPolicy, acknowledgement = null }) {
  const [policy, setPolicy] = useState(propPolicy || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (policy) return; // already have data

    setLoading(true);
    setError("");
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    axios
      .get(`${baseURL}/api/platform-settings/cancellation-policy`)
      .then((res) => {
        setPolicy(res.data);
      })
      .catch(() => {
        setError("Unable to load cancellation policy. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, policy]);

  // Sync if parent passes policy after initial render
  useEffect(() => {
    if (propPolicy && !policy) {
      setPolicy(propPolicy);
    }
  }, [propPolicy, policy]);

  if (!open) return null;

  const policyData = policy?.policy || {};
  const reschedule = policy?.reschedule || null;
  const requiresAcknowledgement = Boolean(acknowledgement);
  const acknowledged = Boolean(acknowledgement?.accepted);
  const acknowledgementBlocked = requiresAcknowledgement && !acknowledged;

  // One reading of the fee schedule. See utils/cancellationPolicyTiers.js for why this is not duplicated here.
  const feeTiers = buildDisplayTiers(policyData);

  const tierSummary = feeTiers.map((tier, index) => {
    const feePercent = Math.max(0, Number(tier?.feePercent) || 0);
    const refundPercent = Math.max(0, 100 - feePercent);

    return {
      label: formatTierLabel(tier, index, feeTiers),
      feePercent,
      refundPercent,
    };
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cancellation Policy"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "28px 24px",
          maxWidth: "480px",
          width: "100%",
          // The reschedule section made this modal taller than a phone viewport; without this the
          // accept checkbox and the Got it button sat below the fold with no way to scroll to them.
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "20px",
          }}
        >
          <h2
            style={{ fontSize: "20px", fontWeight: "700", margin: 0, color: "#111827" }}
          >
            📋 Cancellation &amp; Reschedule Policy
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              color: "#6b7280",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <p style={{ color: "#6b7280", textAlign: "center" }}>
            Loading policy…
          </p>
        )}

        {error && (
          <p style={{ color: "#dc2626", textAlign: "center" }}>{error}</p>
        )}

        {!loading && !error && (
          <>
            {/* Description */}
            <div
              style={{
                fontSize: "14px",
                color: "#374151",
                lineHeight: "1.6",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px 0" }}>
                Find our cancellation fees, refunds and reschedule terms below.
              </p>
              <p style={{ margin: 0 }}>
                Your refund depends on how much notice you give before the appointment, based on the current platform cancellation settings.
              </p>
            </div>

            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "10px",
                padding: "12px 14px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#9a3412",
                  margin: "0 0 6px 0",
                }}
              >
                Important Payment Notice
              </p>
              <p
                style={{
                  fontSize: "13px",
                  color: "#7c2d12",
                  lineHeight: "1.55",
                  margin: "0 0 6px 0",
                }}
              >
                {NON_REFUNDABLE_PROCESSING_FEE_NOTICE}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9a3412",
                  lineHeight: "1.5",
                  margin: 0,
                }}
              >
                {NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE}
              </p>
            </div>

            {/* ── RESCHEDULING (2026-11-04) ───────────────────────────────────────────────────────
                The fee that was charged but never disclosed. Both sentences come from the server
                (`describeReschedulePolicy`), so the percentage here is the percentage the charge uses —
                an admin raising `lateRescheduleFeePercent` changes this text with no deploy. */}
            {reschedule && (
              <>
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "10px",
                  }}
                >
                  Rescheduling
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#f2fbf5",
                      border: "1px solid #c6ebd4",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#166534",
                      lineHeight: 1.6,
                    }}
                  >
                    {reschedule.freeText}
                  </div>
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#f7fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#4b5563",
                      lineHeight: 1.6,
                    }}
                  >
                    {reschedule.lateText}
                  </div>
                </div>
              </>
            )}

            {/* Fee tiers */}
            {feeTiers.length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "10px",
                  }}
                >
                  Cancellation Fees And Refunds
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "24px",
                  }}
                >
                  {feeTiers.map((tier, idx) => {
                    const summary = tierSummary[idx];
                    const feeLabel =
                      summary.feePercent <= 0
                        ? "No cancellation fee"
                        : `${summary.feePercent}% fee`;
                    const refundLabel = `${summary.refundPercent}% refund`;
                    return (
                      <div
                        key={`${summary.label}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 14px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      >
                        <span style={{ color: "#374151" }}>{summary.label}</span>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontWeight: "700",
                              color:
                                summary.feePercent <= 0 ? "#059669" : "#dc2626",
                            }}
                          >
                            {feeLabel}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {refundLabel}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── THE ACKNOWLEDGEMENT LIVES HERE NOW (2026-11-04) ──────────────────────────────────
                It used to be a checkbox on the summary page next to the Pay button, which meant a customer
                could tick it without ever opening the policy it referred to. Ticking it here requires the
                modal to be open, and the modal is the only place the fee schedule is shown. */}
            {requiresAcknowledgement && (
              <label
                htmlFor="policy-modal-accept"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginBottom: "14px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  background: acknowledged ? "#f2fbf5" : "#f7fafc",
                  border: `1px solid ${acknowledged ? "#c6ebd4" : "#e2e8f0"}`,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  id="policy-modal-accept"
                  checked={acknowledged}
                  onChange={(e) => acknowledgement?.onAccept?.(e.target.checked)}
                  style={{ marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: "#374151",
                    lineHeight: 1.5,
                    userSelect: "none",
                  }}
                >
                  I have read and accept the Cancellation &amp; Reschedule Policy, including the
                  cancellation fee schedule, the non-refundable processing fees, and the reschedule fee
                  that applies inside the free window.
                </span>
              </label>
            )}

            <button
              onClick={onClose}
              disabled={acknowledgementBlocked}
              style={{
                width: "100%",
                padding: "12px",
                background: acknowledgementBlocked
                  ? "#cbd5e1"
                  : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontWeight: "600",
                fontSize: "15px",
                cursor: acknowledgementBlocked ? "not-allowed" : "pointer",
              }}
            >
              {acknowledgementBlocked ? "Please tick the box above to continue" : "Got it"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CancellationPolicyModal;
